import React, { useState } from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const ChevronDown = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

function AccordionItem({ title, children, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        className="accordion-button flex w-full items-center justify-between py-5 md:py-6 text-left"
      >
        <span className="text-lg font-semibold text-gray-900">{title}</span>
        <ChevronDown 
            className={`accordion-icon w-5 h-5 text-gray-500 transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`} 
        />
      </button>

      <div
        className={`grid overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="pb-6 text-gray-600 leading-relaxed space-y-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TermsAndConditionsPage() {
  const [openIndexes, setOpenIndexes] = useState([]);

  const handleToggle = (index) => {
    setOpenIndexes(prevIndexes => 
      prevIndexes.includes(index) 
        ? prevIndexes.filter(i => i !== index) 
        : [...prevIndexes, index]
    );
  };

  const termsSections = [
    {
      title: "1. Scope of Services",
      content: (
        <>
          <p>Techvelly provides the following services:</p>
          <ul className="list-disc list-inside mb-4">
            <li>Web/Mobile App Development</li>
            <li>Custom Software Development</li>
            <li>Digital Marketing and Branding</li>
            <li>IT Consultation and Support</li>
            <li>Graphic Design and UI/UX Design</li>
          </ul>
        </>
      )
    },
    {
      title: "2. Project Scope and Procedure",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>A written contract must be signed before the start of every project.</li>
          <li>The project's scope, timeline, and technology will be defined.</li>
          <li>Additional charges will apply for changes in scope.</li>
        </ul>
      )
    },
    {
      title: "3. Payment and Billing",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>Payment will be made in stages (30%-40%-30%).</li>
          <li>Invoices must be paid within the specified time (7 working days).</li>
          <li>Additional charges may be imposed for late payment.</li>
        </ul>
      )
    },
    {
      title: "4. Ownership and Intellectual Property",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>All source code and content will belong to the client after the final payment.</li>
          <li>Techvelly's code/design cannot be used without third-party permission.</li>
        </ul>
      )
    },
    {
      title: "5. Confidentiality",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>All client information will be kept confidential.</li>
          <li>An NDA (Non-Disclosure Agreement) must be followed.</li>
        </ul>
      )
    },
    {
      title: "6. Support and Maintenance",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>1–3 months of free technical support after project completion.</li>
          <li>Monthly/annual charges apply for additional support.</li>
        </ul>
      )
    },
    {
      title: "7. Disclaimer (Indemnification)",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>Techvelly is not responsible for natural disasters, internet outages, or third-party software errors.</li>
          <li>Techvelly will not take responsibility for data loss or configuration errors caused by the client.</li>
        </ul>
      )
    },
    {
      title: "8. Service Cancellation and Refund Policy",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>If the client cancels the agreement, the amount paid is non-refundable.</li>
          <li>A partial refund is applicable if Techvelly violates the agreement.</li>
        </ul>
      )
    }
  ];

  const customerServiceSections = [
    {
      title: "1. Service Provision Timeline",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-2">General Response Time:</h3>
          <ul className="list-disc list-inside mb-4">
            <li>Email/Ticket response within 24 hours (on working days).</li>
          </ul>
          <h3 className="text-xl font-semibold mb-2">Emergency Support Time:</h3>
          <ul className="list-disc list-inside mb-4">
            <li>Response within 4 hours (as per agreement).</li>
          </ul>
        </>
      )
    },
    {
      title: "2. Support Channels",
      content: (
        <>
          <p>Techvelly provides customer service through the following:</p>
          <ul className="list-disc list-inside mb-4">
            <li>Email: support@techvelly.com</li>
            <li>Helpdesk Ticket System</li>
            <li>Direct Phone Call (limited hours)</li>
            <li>Live Chat (on the website)</li>
            <li>Screen sharing sessions via Google Meet/Zoom</li>
          </ul>
        </>
      )
    },
    {
        title: "3. Types of Support Services",
        content: (
            <table className="w-full mb-4 border border-gray-300">
                <thead>
                <tr>
                    <th className="text-left border border-gray-300 px-4 py-2">Type of Service</th>
                    <th className="text-left border border-gray-300 px-4 py-2">Description</th>
                    <th className="text-left border border-gray-300 px-4 py-2">Applicable Time</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td className="border border-gray-300 px-4 py-2">Free support</td>
                    <td className="border border-gray-300 px-4 py-2">Minor bug fixes, UI issues within one to three months after project completion</td>
                    <td className="border border-gray-300 px-4 py-2">As per the Standard project contract</td>
                </tr>
                <tr>
                    <td className="border border-gray-300 px-4 py-2">Paid maintenance</td>
                    <td className="border border-gray-300 px-4 py-2">Feature updates, server monitoring, backup</td>
                    <td className="border border-gray-300 px-4 py-2">Monthly and annual subscription</td>
                </tr>
                <tr>
                    <td className="border border-gray-300 px-4 py-2">Emergency support</td>
                    <td className="border border-gray-300 px-4 py-2">Server down, cyber-attacks, urgent security updates</td>
                    <td className="border border-gray-300 px-4 py-2">24/7 (will depend on the contract)</td>
                </tr>
                </tbody>
            </table>
        )
    },
    {
      title: "4. Ticket/Reporting System",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>The customer can submit a ticket for any problem.</li>
          <li>Each ticket is tracked with a unique ID.</li>
          <li>Each ticket is resolved within the specified time according to the SLA (Service Level Agreement).</li>
        </ul>
      )
    },
    {
      title: "5. Service Expectations",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>Techvelly will always maintain a professional and respectful demeanor.</li>
          <li>Appropriate solutions will be applied by understanding the severity of the clients' problems.</li>
          <li>All client data will be kept completely confidential.</li>
        </ul>
      )
    },
    {
      title: "6. Inappropriate Use and Limitations",
      content: (
        <>
          <p>Techvelly will not tolerate the following behavior during customer service:</p>
          <ul className="list-disc list-inside mb-4">
            <li>Abusive language, threats, or defamatory remarks.</li>
            <li>Irrelevant or unethical requests.</li>
            <li>Misuse or abuse of the system.</li>
          </ul>
          <p>If such behavior is encountered, Techvelly reserves the right to suspend the customer's service temporarily or permanently.</p>
        </>
      )
    },
    {
      title: "7. Rating and Feedback",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li>The customer can provide a rating after each support interaction.</li>
          <li>Techvelly will improve the quality of service by analyzing feedback.</li>
        </ul>
      )
    },
    {
      title: "8. Custom Support Packages (Optional)",
      content: (
        <ul className="list-disc list-inside mb-4">
          <li><strong>Basic:</strong> Up to 10 tickets per month.</li>
          <li><strong>Pro:</strong> Includes 24/7 Emergency Support.</li>
          <li><strong>Enterprise:</strong> Priority response, dedicated account manager.</li>
        </ul>
      )
    }
  ];

  return (
    <>
    <Header />
    <div className="bg-gray-50 text-gray-800 min-h-screen font-sans">
      <div className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
        
        <header className="mb-10 md:mb-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 mt-10">
            Techvelly Terms of Service
          </h1>
          <p className="text-sm text-gray-500 mt-4">
            Last Updated: May 23, 2025
          </p>
        </header>

        <div className="bg-white p-6 md:p-10 rounded-2xl shadow-lg">
          <div className="mt-6">
            {termsSections.map((section, index) => (
              <AccordionItem
                key={index}
                title={section.title}
                isOpen={openIndexes.includes(index)}
                onToggle={() => handleToggle(index)}
              >
                {section.content}
              </AccordionItem>
            ))}
          </div>
        </div>

        <header className="mb-10 md:mb-16 text-center mt-20">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Techvelly Customer Service Policy
          </h1>
        </header>

        <div className="bg-white p-6 md:p-10 rounded-2xl shadow-lg">
          <div className="mt-6">
            {customerServiceSections.map((section, index) => (
              <AccordionItem
                key={index}
                title={section.title}
                isOpen={openIndexes.includes(index + termsSections.length)}
                onToggle={() => handleToggle(index + termsSections.length)}
              >
                {section.content}
              </AccordionItem>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 md:p-10 rounded-2xl shadow-lg mt-10">
          <div className="pt-8 mt-8 border-t border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">9. Amendments</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms may be updated at any time. The client will be notified in case of changes.
            </p>
          </div>

          <div className="pt-8 mt-8 border-t border-gray-200">
             <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Contact</h2>
             <p className="text-gray-600 leading-relaxed">
                Techvelly IT
             </p>
             <ul className="list-disc list-inside pl-4 space-y-1">
                <li>Email: <a href="mailto:info@techvelly.com" className="text-indigo-600 hover:text-indigo-700 font-medium transition-colors">info@techvelly.com</a></li>
                <li>Mobile: +8801927-046394</li>
                <li>Website: <a href="https://www.techvelly.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-medium transition-colors">www.techvelly.com</a></li>
              </ul>
          </div>
        </div>

      </div>
    </div>
    <Footer />
    </>
  );
}